// Módulo del área analista: el panel de la cartera (PanelAnalista) y la ficha
// de una empresa de la cartera (CompanyInfo y sus tabs). Sólo companyDetail
// vive en el state — es la basicData compartida que la vista padre pasa por
// props a todos los tabs. El resto (cartera, plan de cuentas, atributos,
// usuarios, archivos) es data local de cada vista: las actions delegan en la
// api y DEVUELVEN la data para que la vista la consuma; los errores se
// propagan para que cada vista conserve su manejo actual
// (notificate / consoleHandlerError).
import {
    addAttributes as addAttributesApi,
    applyTemplateExcelDiff as applyTemplateExcelDiffApi,
    approveAccountPlan as approveAccountPlanApi,
    compareTemplateExcel as compareTemplateExcelApi,
    createAccount as createAccountApi,
    deleteAccount as deleteAccountApi,
    deleteAttributes as deleteAttributesApi,
    deleteUploadedFile as deleteUploadedFileApi,
    disconnectCompanyConnection as disconnectCompanyConnectionApi,
    getCompanyConnections,
    getConnectionsMismatches,
    reconcileAllConnections as reconcileAllConnectionsApi,
    reconcileCompanyConnections as reconcileCompanyConnectionsApi,
    createTemplateAccount as createTemplateAccountApi,
    deleteTemplateAccount as deleteTemplateAccountApi,
    editAccount as editAccountApi,
    editTemplateAccount as editTemplateAccountApi,
    fetchUnlockChallenge as fetchUnlockChallengeApi,
    lockAccountPlan as lockAccountPlanApi,
    unlockAccountPlan as unlockAccountPlanApi,
    getAccountPlanForCompany,
    getAttributes,
    getCarteraAnalista,
    getBusinessTypeClassificationCheck,
    getDetalleCompania,
    getTemplateAccountPlan,
    getTemplateBusinessTypes,
    getTemplateSharedAccounts,
    getUsersByCompany,
    get_account_plan_by_company_ordered,
    linkRequests,
    makeUniquePlanAccounts as makeUniquePlanAccountsApi,
    saveTipoNegocio,
    splitTemplateSharedAccounts as splitTemplateSharedAccountsApi,
    upload_all_plan_accounts,
    uploadFile,
} from "@/api/carteraAnalista";

const state = () => ({
    companyDetail: null,
    companyDetailLoading: false,
    companyDetailError: null,
});

const mutations = {
    SET_COMPANY_DETAIL: (state, payload) => {
        state.companyDetail = payload;
    },
    SET_COMPANY_DETAIL_LOADING: (state, payload) => {
        state.companyDetailLoading = payload;
    },
    SET_COMPANY_DETAIL_ERROR: (state, payload) => {
        state.companyDetailError = payload;
    },
};

const actions = {
    // Ficha completa de la empresa (basicData). Commitea loading/error y
    // RELANZA el error: la vista decide cómo reportarlo.
    async fetchCompanyDetail({ commit }, companyId) {
        commit("SET_COMPANY_DETAIL_LOADING", true);
        commit("SET_COMPANY_DETAIL_ERROR", null);
        try {
            const { data } = await getDetalleCompania(companyId, true);
            commit("SET_COMPANY_DETAIL", data);
            return data;
        } catch (error) {
            commit("SET_COMPANY_DETAIL_ERROR", error);
            throw error;
        } finally {
            commit("SET_COMPANY_DETAIL_LOADING", false);
        }
    },

    // ── Panel del analista (cartera y solicitudes de alta) ──────────────────
    async fetchCartera() {
        return (await getCarteraAnalista()).data;
    },
    async fetchLinkRequests() {
        return (await linkRequests()).data;
    },

    // ── Gestión de terceros (conexiones Fintoc de la empresa) ───────────────
    async fetchCompanyConnections(_, companyId) {
        return (await getCompanyConnections(companyId)).data;
    },
    async disconnectCompanyConnection(_, { companyId, cplId, localOnly = false }) {
        return (
            await disconnectCompanyConnectionApi(companyId, cplId, localOnly)
        ).data;
    },
    async reconcileCompanyConnections(_, companyId) {
        return (await reconcileCompanyConnectionsApi(companyId)).data;
    },

    // ── Desfases BD ↔ Fintoc (vista org-wide del panel) ─────────────────────
    async fetchConnectionsMismatches() {
        return (await getConnectionsMismatches()).data;
    },
    async reconcileAllConnections() {
        return (await reconcileAllConnectionsApi()).data;
    },

    // ── Plan de cuentas (tab Plan de Cuentas) ───────────────────────────────
    async fetchAccountPlans(_, companyId) {
        return (await getAccountPlanForCompany(companyId)).data;
    },
    // Plan de cuentas ordenado para la vista cliente (AccountsPlan).
    async fetchOrderedAccountPlan(_, data) {
        return (await get_account_plan_by_company_ordered(data)).data;
    },
    async createAccount(
        _,
        { companyId, level2Id, accountName, order, balanceConcept },
    ) {
        return (
            await createAccountApi(
                companyId, level2Id, accountName, order, balanceConcept,
            )
        ).data;
    },
    async editAccount(
        _,
        { companyId, accountId, level2Id, accountName, order, balanceConcept },
    ) {
        return (
            await editAccountApi(
                companyId, accountId, level2Id, accountName, order, balanceConcept,
            )
        ).data;
    },
    async deleteAccount(_, { companyId, accountId }) {
        return (await deleteAccountApi(companyId, accountId)).data;
    },
    async approveAccountPlan(_, companyId) {
        return (await approveAccountPlanApi(companyId)).data;
    },
    async makeUniquePlanAccounts(_, companyId) {
        return (await makeUniquePlanAccountsApi(companyId)).data;
    },
    async uploadAllPlanAccounts(_, { companyId, formData }) {
        return (await upload_all_plan_accounts(companyId, formData)).data;
    },
    // Candado del plan de cuentas (docs/decisiones §30). Los 403/400 del
    // backend viajan con {detail}: la vista/modal los muestra tal cual.
    async lockAccountPlan(_, companyId) {
        return (await lockAccountPlanApi(companyId)).data;
    },
    async fetchUnlockChallenge(_, companyId) {
        return (await fetchUnlockChallengeApi(companyId)).data;
    },
    async unlockAccountPlan(_, { companyId, email, code, reason }) {
        return (await unlockAccountPlanApi(companyId, { email, code, reason })).data;
    },

    // ── Plantillas estándar de plan de cuentas (vista StandardAccountPlan) ──
    async fetchTemplateBusinessTypes() {
        return (await getTemplateBusinessTypes()).data;
    },
    async fetchTemplateAccountPlan(_, businessTypeId) {
        return (await getTemplateAccountPlan(businessTypeId)).data;
    },
    async createTemplateAccount(
        _,
        { businessTypeId, level2Id, accountName, order, balanceConcept },
    ) {
        return (
            await createTemplateAccountApi(
                businessTypeId, level2Id, accountName, order, balanceConcept,
            )
        ).data;
    },
    async editTemplateAccount(
        _,
        { businessTypeId, accountId, level2Id, accountName, order, balanceConcept },
    ) {
        return (
            await editTemplateAccountApi(
                businessTypeId, accountId, level2Id, accountName, order,
                balanceConcept,
            )
        ).data;
    },
    async deleteTemplateAccount(_, { businessTypeId, accountId }) {
        return (await deleteTemplateAccountApi(businessTypeId, accountId)).data;
    },
    async fetchTemplateSharedAccounts() {
        return (await getTemplateSharedAccounts()).data;
    },
    async splitTemplateSharedAccounts(_, accountPlanIds = null) {
        return (await splitTemplateSharedAccountsApi(accountPlanIds)).data;
    },
    async compareTemplateExcel(_, file) {
        return (await compareTemplateExcelApi(file)).data;
    },
    async applyTemplateExcelDiff(_, operations) {
        return (await applyTemplateExcelDiffApi(operations)).data;
    },

    // ── Categorías / atributos (tab Categorías) ─────────────────────────────
    async fetchAttributes(_, companyId) {
        return (await getAttributes(companyId)).data;
    },
    async addAttributes(_, attribute) {
        return (await addAttributesApi(attribute)).data;
    },
    // Devuelve la response COMPLETA: la vista decide por status (204 = ok).
    deleteAttributes(_, { attribute, companyId }) {
        return deleteAttributesApi(attribute, companyId);
    },

    // ── Datos básicos (tab Datos básicos) ───────────────────────────────────
    async fetchUsersByCompany(_, companyId) {
        return (await getUsersByCompany(companyId)).data;
    },
    async deleteUploadedFile(_, companyId) {
        return (await deleteUploadedFileApi(companyId)).data;
    },
    async uploadCompanyFile(_, { companyId, file }) {
        return (await uploadFile(companyId, file)).data;
    },

    // ── Subida de datos (tab Subida de datos) ───────────────────────────────

    // ── Tipo de negocio (tab Tipo de negocio) ───────────────────────────────
    async saveBusinessType(_, { companyId, checked, classifyBySales }) {
        return (await saveTipoNegocio(companyId, checked, classifyBySales)).data;
    },

    // Validador super_type ↔ clasificación (H1/F6): el tab lo consulta al
    // abrir y tras guardar, y muestra el warning si hay mismatch.
    async fetchBusinessTypeCheck(_, companyId) {
        return (await getBusinessTypeClassificationCheck(companyId)).data;
    },
};

export default { namespaced: true, state, mutations, actions };
